/**
 * PatientSummaryModal
 * Modal para o administrador visualizar resumo do paciente (prontuário)
 */
import { useEffect, useState } from 'react';
import {
  X,
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Activity,
  Pill,
  AlertTriangle,
  Loader2,
  FileText,
  ClipboardList,
  Eye,
  Download,
} from 'lucide-react';
import { getPatientMedicalHistory } from '@/services/patients';
import { getPatientDocuments } from '@/services/documents';
import { downloadPatientRecordPdf } from '@/services/pdfExports';
import type { PatientDTO, MedicalHistoryDTO } from '@/services/patients';
import type { DocumentDTO } from '@/services/documents';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

interface PatientSummaryModalProps {
  patient: PatientDTO | null;
  isOpen: boolean;
  onClose: () => void;
  onViewDocument?: (doc: DocumentDTO) => void;
}

export function PatientSummaryModal({ patient, isOpen, onClose, onViewDocument }: PatientSummaryModalProps) {
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistoryDTO | null>(null);
  const [documents, setDocuments] = useState<DocumentDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  useEffect(() => {
    if (!isOpen || !patient) return;

    setIsLoading(true);
    setMedicalHistory(null);

    getPatientMedicalHistory(patient.id)
      .then((res) => {
        if (res.success && res.data) {
          setMedicalHistory(res.data);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));

    setDocsLoading(true);
    getPatientDocuments(patient.userId || patient.user?.id || patient.id)
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setDocuments(res.data);
        } else {
          setDocuments([]);
        }
      })
      .catch(() => setDocuments([]))
      .finally(() => setDocsLoading(false));
  }, [isOpen, patient?.id]);

  if (!isOpen || !patient) return null;

  const name = patient.user?.name || 'N/A';
  const email = patient.user?.email || 'N/A';
  const patientUserId = patient.userId || patient.user?.id || '';

  const handleExportPdf = async () => {
    if (!patientUserId) {
      toast.error('Identificação do paciente não encontrada.');
      return;
    }

    try {
      setIsExportingPdf(true);
      await downloadPatientRecordPdf(patientUserId);
      toast.success('PDF do prontuário gerado com sucesso.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao gerar PDF do prontuário.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const formatDate = (date?: string) => {
    if (!date) return '—';
    try {
      return format(new Date(date), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return '—';
    }
  };

  const splitClinicalList = (value?: string | string[]): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    return value
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const allergies = splitClinicalList(medicalHistory?.allergies);
  const medications = splitClinicalList(medicalHistory?.medications);

  const getGenderLabel = (g?: string) => {
    if (g === 'M') return 'Masculino';
    if (g === 'F') return 'Feminino';
    if (g === 'Outro') return 'Outro';
    return '—';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 w-full max-w-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-neutral-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">
              {name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">{name}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Resumo do Paciente</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Dados Pessoais */}
          <section>
            <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Dados Pessoais
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                <p className="text-xs text-gray-400 mb-0.5">Nome</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{name}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                <p className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
                  <Mail className="w-3 h-3" /> E-mail
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{email}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                <p className="text-xs text-gray-400 mb-0.5">CPF</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{patient.cpf || '—'}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                <p className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> Nascimento
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatDate(patient.dateOfBirth)}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                <p className="text-xs text-gray-400 mb-0.5">Sexo</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{getGenderLabel(patient.gender)}</p>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                <p className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Telefone
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{patient.phone || '—'}</p>
              </div>
            </div>
          </section>

          {/* Endereço */}
          {(patient.city || patient.street) && (
            <section>
              <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Endereço
              </h3>
              <div className="p-3 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {[patient.street, patient.number, patient.complement, patient.city, patient.state, patient.zipCode]
                    .filter(Boolean)
                    .join(', ') || '—'}
                </p>
              </div>
            </section>
          )}

          {/* Documentos */}
          <section>
            <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Documentos Anexados
            </h3>
            
            {docsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 text-teal-600 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Carregando documentos...</span>
              </div>
            ) : documents.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="truncate">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{doc.documentType}</p>
                        <p className="text-xs text-gray-500">{formatDate(doc.createdAt)}</p>
                      </div>
                    </div>
                    {onViewDocument && (
                      <button 
                        onClick={() => onViewDocument(doc)}
                        className="p-2 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-lg transition-colors"
                        title="Visualizar documento"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-gray-50 dark:bg-neutral-800/50 rounded-xl border border-gray-100 dark:border-neutral-800 text-center">
                <p className="text-sm text-gray-500">Nenhum documento encontrado.</p>
              </div>
            )}
          </section>

          {/* Histórico Médico */}
          <section>
            <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Prontuário / Histórico Médico
            </h3>

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-teal-600 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Carregando prontuário...</span>
              </div>
            ) : medicalHistory ? (
              <div className="space-y-3">
                {medicalHistory.medicalHistory && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-blue-600" />
                      <p className="text-xs font-bold text-blue-800 dark:text-blue-300 uppercase">Histórico Clínico</p>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {medicalHistory.medicalHistory}
                    </p>
                  </div>
                )}

                {allergies.length > 0 && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                      <p className="text-xs font-bold text-red-800 dark:text-red-300 uppercase">Alergias</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {allergies.map((a, i) => (
                        <span key={i} className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-xs font-medium">
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {medications.length > 0 && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/30 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Pill className="w-4 h-4 text-amber-600" />
                      <p className="text-xs font-bold text-amber-800 dark:text-amber-300 uppercase">Medicamentos em Uso</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {medications.map((m, i) => (
                        <span key={i} className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg text-xs font-medium">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {!medicalHistory.medicalHistory &&
                  allergies.length === 0 &&
                  medications.length === 0 && (
                    <div className="py-6 text-center text-gray-400 dark:text-gray-500">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">Prontuário vazio — nenhum dado registrado ainda.</p>
                    </div>
                  )}
              </div>
            ) : (
              <div className="py-6 text-center text-gray-400 dark:text-gray-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Prontuário não disponível ou ainda não preenchido.</p>
              </div>
            )}
          </section>

          {/* Cadastro */}
          <div className="text-xs text-gray-400 dark:text-gray-600 text-right">
            Paciente desde {formatDate(patient.createdAt)}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 dark:bg-neutral-800/50 border-t border-gray-200 dark:border-neutral-800 flex justify-end gap-2">
          <button
            onClick={handleExportPdf}
            disabled={isExportingPdf || !patientUserId}
            className="px-4 py-2 text-sm font-medium text-teal-700 dark:text-teal-300 bg-white dark:bg-neutral-800 border border-teal-200 dark:border-teal-800 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {isExportingPdf ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {isExportingPdf ? 'Gerando PDF...' : 'Exportar PDF'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
